// VideoPanel.jsx

import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { getFullBodyInBox } from "./Services/MovementService";
import { FULL_SCREEN_SKELETON_COLOR } from "../../../utils/Exercise/ExerciseUtils";
import GLOBAL from "../../../global";
import { useTenantFeatures } from "../../../features/tenantFeatures/useTenantFeature";
import UserMediaWebcam from "./UserMediaWebcam";
import { Pose, POSE_CONNECTIONS } from "@mediapipe/pose";
import {
  getShoulderMidPoint,
  processPhase,
  lightingAnalyzer,
  calibration_monitoring,
  checkPersonVisibility,
  detectMultiplePersons,
  loadMoveNetDetector,
  posture_side_view_monitoring,
  person_orientation_check,
} from "./utilities/ExerciseUtils";
import VideoRecorder from "./Services/VideoRecorder";
import CONSTANT from "../../../constants/Constant";
import { playAudio, getAudioUrl, forceStopAllAudio } from "../../../utils/Utils";
import audioManager from "../../../utils/AudioManager";

const VideoPanel = ({
  startRecording,
  stateRef,
  setRepCount,
  onStopVideoRecording,
  webcamConstraints,
  messageOnVideo,
  setMessageOnVideo,
  totalSeconds = 0,
  selectedExercise,
  movementRequirementsRef,
  selectedExerciseState,
  realTimeDetectionConstrains,
  forceCloseReviewMovementModalRef,
  onMultiPersonDetected,
  onRecordingInterrupted,
  openVideoModal,
  isMovementPaused,
  showPreparationInstructionModal,
  isAnyModalOpenRef,
  isCalibrationProcessing,
  isAssessmentComplete,
  isExamineticsEnabled,
  detectionBridgeRef,
  imperativeRecordingRef,
  exerciseGeneration,
  onExamineticsFullBodyNotVisible,
  resetVideoPanelStateRef,
}) => {
  const [count, setCount] = useState(1);
  // const [poseLandmarks, setPoseLandmarks] = useState([]);

  const { videoFrameRate, isRealtimeFeedbackROMEnable: _isRealtimeFeedbackROMEnable, isProperPersonPositionEnable: _isProperPersonPositionEnable, isMultiPersonDetectionEnable: _isMultiPersonDetectionEnable, enableAutoStopOnOutOfFrame: _enableAutoStopOnOutOfFrame } = useTenantFeatures();
  const maxVideoFrameRate = Number(videoFrameRate) || 30;
  const isRealtimeFeedbackROMEnable = _isRealtimeFeedbackROMEnable;
  const isProperPersonPositionEnable = _isProperPersonPositionEnable;
  const isMultiPersonDetectionEnable = _isMultiPersonDetectionEnable;
  const isEnableAutoStopOnOutOfFrame = _enableAutoStopOnOutOfFrame;

  // refs
  const moveNetDetectorRef = useRef(null);
  const poseModelRef = useRef(null);
  const webcamRef = useRef(null);
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const requestRef = useRef(null);
  const firstPoseLogged = useRef(false);
  const isCameraSwitchingRef = useRef(false);
  const isProcessingRef = useRef(false);

  // interval refs (only multi-person keeps its interval - uses separate MoveNet detector)
  const multiPersonIntervalRef = useRef(null); // 1000ms - multi-person during recording

  // Dead refs - kept for backward compat with check functions that clear them on completion.
  // These are never assigned to, so the cleanup code in check functions is a harmless no-op.
  const movementSpecificIntervalRef = useRef(null);

  // session-level state (persists across all movements)
  const sessionLightCheckDoneRef = useRef(false);
  const lastLightAudioTimeRef = useRef(0);
  const LIGHT_AUDIO_COOLDOWN_MS = 3000;

  // movement-level state (resets per movement)
  const movementStateRef = useRef({
    personInBoxConfirmed: false,
    isReadyToRecord: false,
  });

  // tracks whether recording for the current movement is active (resets on move to next movement)
  const isCurrentMovementRecordingRef = useRef(false);

  // recording-level state
  const multiPersonCheckCountRef = useRef(0);
  const isDetectorLoadingRef = useRef(false); // Track if detector is currently being loaded

  const poseLandmarksRef = useRef([]);
  const segmentationMaskRef = useRef(null);
  const frameCountRef = useRef(0);
  const lastCanvasDimsRef = useRef({ width: 0, height: 0 });
  // add a ref to track if we're in the delay period

  // Person not in box audio tracking
  const notInBoxAudioCountRef = useRef(0);
  const breakPausePlayedRef = useRef(false);
  const isNotInBoxAudioPlayingRef = useRef(false);

  // Calibration debouncing - prevent false triggers from momentary "good" frames
  const calibrationGoodTimestampRef = useRef(0);
  const CALIBRATION_DEBOUNCE_MS = 500; // Require 500ms of sustained "good" state

  // Time-based throttle for requirement checks (frame-rate independent)
  const lastCheckTimeRef = useRef(0);
  const REQUIREMENT_CHECK_INTERVAL_MS = 500;

  // Posture debouncing - same logic as calibration
  const postureGoodTimestampRef = useRef(0);
  const POSTURE_DEBOUNCE_MS = 500; // Require 500ms of sustained "good" state

  // Orientation debouncing - same pattern as calibration/posture
  const orientationGoodTimestampRef = useRef(0);

  // Re-entry debounce — wait 500ms after person returns before validating
  const reentryTimestampRef = useRef(0);
  const personWasOutOfBoxRef = useRef(false);
  const REENTRY_DEBOUNCE_MS = 3000;

  const isShowCountDownRef = useRef(false);

  const lastAnnouncementTimeRef = useRef(0);
  const ANNOUNCEMENT_COOLDOWN_MS = 2000;

  const ORIENTATION_DEBOUNCE_MS = 500; // Require 500ms of sustained correct orientation
  const ALLOWED_FACE_ORIENTATIONS = [
    CONSTANT.FACE_ORIENTATION.LEFT,
    CONSTANT.FACE_ORIENTATION.RIGHT,
    CONSTANT.FACE_ORIENTATION.FRONT,
  ];

  const isLightMonitorEnable = false;

  // Examinetics out-of-box/full body warning flow tracking
  const examineticsOutOfBoxStartTimestampRef = useRef(0);
  const examineticsAnnouncementCountRef = useRef(0);
  const examineticsLastAnnouncementTimestampRef = useRef(0);
  const examineticsPopupShownRef = useRef(false);
  const examineticsAudioPlayingRef = useRef(false);


  const clearAllIntervals = () => {
    if (multiPersonIntervalRef.current) {
      clearInterval(multiPersonIntervalRef.current);
    }

    // force-stop audio and prevent retry loops from resurrecting it
    forceStopAllAudio();
    multiPersonIntervalRef.current = null;
  };

  // get specific message by both category keytext and message keytext
  const getAudioByKeyTexts = (
    realTimeDetectionConstrains,
    categoryKeyText,
    messageKeyText,
  ) => {
    const category = realTimeDetectionConstrains.RealTimeDetection.find(
      (item) => item.KeyText === categoryKeyText,
    );

    if (!category) return null;

    const result = category.RealTimeDetectionList.find(
      (item) => item.KeyText === messageKeyText,
    );

    return result?.AudioUrl || "";
  };

  const announceRealTimeFeedbackAudio = (audio) => {
    if (audio) {
      const isCarryTest = selectedExercise.current?.ExerciseCodeName === 'carrytest';
      if (isExamineticsEnabled && isCarryTest && (isShowCountDownRef.current || startRecording.current || detectionBridgeRef?.current?.fsmState === 'USER_READY_FOR_PRE_RECORDING')) {
          return;
      }

      // Don't interrupt currently playing audio
      if (audioManager.getIsPlaying()) return;

      const now = Date.now();
      if (now - lastAnnouncementTimeRef.current < ANNOUNCEMENT_COOLDOWN_MS) {
        return;
      }

      lastAnnouncementTimeRef.current = now;
      playAudio(audio);
    }
  };

  const canAnnounceFeedbackNow = () =>
    isRealtimeFeedbackROMEnable &&
    !isMovementPaused.current &&
    !openVideoModal.current &&
    !isCalibrationProcessing.current &&
    (!isExamineticsEnabled || !isShowCountDownRef.current || selectedExercise.current?.ExerciseCodeName !== 'carrytest') &&
    !startRecording.current &&
    GLOBAL.IS_PATIENT_IN_BOX;

  const shouldAnnounceRealtimeFeedbackNow = () =>
    canAnnounceFeedbackNow() && isProperPersonPositionEnable;

  const shouldAnnounceMultiPersonFeedbackNow = () =>
    canAnnounceFeedbackNow() && isMultiPersonDetectionEnable;

  const performLightCheck = () => {
    // Early return if light monitoring is disabled
    if (!isLightMonitorEnable) {
      console.log("light monitoring disabled, skipping check");
      movementRequirementsRef.current.isGoodLight = true;
      sessionLightCheckDoneRef.current = true;
      return;
    }

    if (!canvasRef.current) return;

    const canvasCtx = canvasRef.current.getContext("2d");
    if (!canvasCtx) return;

    try {
      const imageDataFrame = canvasCtx.getImageData(
        0,
        0,
        canvasRef.current.width,
        canvasRef.current.height,
      );
      const result = lightingAnalyzer(imageDataFrame);
      const { status } = result || {};
      const isGoodLight = ["proper", "good"].includes(status);

      console.log('[DETECTION] light check result:', status, 'isGood:', isGoodLight);

      if (isGoodLight) {
        movementRequirementsRef.current.isGoodLight = true;
        sessionLightCheckDoneRef.current = true;
      } else {
        movementRequirementsRef.current.isGoodLight = false;
        // Play light audio feedback even without person in box
        // (light condition affects the whole frame, not just the person)
        const canPlayAudio = !isMovementPaused.current &&
          !openVideoModal.current &&
          !isCalibrationProcessing.current &&
          (!isExamineticsEnabled || !isShowCountDownRef.current || selectedExercise.current?.ExerciseCodeName !== 'carrytest') &&
          !startRecording.current;

        if (canPlayAudio && Date.now() - lastLightAudioTimeRef.current >= LIGHT_AUDIO_COOLDOWN_MS) {
          lastLightAudioTimeRef.current = Date.now();
          const audio = getAudioByKeyTexts(
            realTimeDetectionConstrains,
            "light",
            result.status,
          );
          console.log('[AUDIO] playing light audio for status:', result.status, 'audioUrl:', audio);
          announceRealTimeFeedbackAudio(audio);
        }
      }
    } catch (error) {
      console.error("error in light check:", error);
    }
  };

  // update resetMovementLevelState to also reset the started flag
  const resetMovementLevelState = () => {
    console.log("=== resetting movement-level state ===");

    // reset movement state
    movementStateRef.current = {
      personInBoxConfirmed: false,
      isReadyToRecord: false,
    };

    movementRequirementsRef.current.isReadyForCalibration = false;
    // Reset isCorrectOrientation to force re-check
    movementRequirementsRef.current.isCorrectOrientation = false;
    forceCloseReviewMovementModalRef.current = false;

    // Reset debounce timers for calibration, posture, and orientation
    calibrationGoodTimestampRef.current = 0;
    postureGoodTimestampRef.current = 0;
    orientationGoodTimestampRef.current = 0;

    // Reset not-in-box audio counters
    notInBoxAudioCountRef.current = 0;
    breakPausePlayedRef.current = false;
    isNotInBoxAudioPlayingRef.current = false;

    firstPoseLogged.current = false;
    isCurrentMovementRecordingRef.current = false;

    // Reset Examinetics out-of-box flow tracking
    examineticsOutOfBoxStartTimestampRef.current = 0;
    examineticsAnnouncementCountRef.current = 0;
    examineticsLastAnnouncementTimestampRef.current = 0;
    examineticsPopupShownRef.current = false;
    examineticsAudioPlayingRef.current = false;
  };

  useEffect(() => {
    if (resetVideoPanelStateRef) {
      resetVideoPanelStateRef.current = resetMovementLevelState;
    }
  }, [resetVideoPanelStateRef]);

  const checkPersonInBox = () => {
    const isPersonInBox = GLOBAL.IS_PATIENT_IN_BOX;

    if (isPersonInBox) {
      console.log("person confirmed in box, stopping person-in-box checks");
      movementStateRef.current.personInBoxConfirmed = true;
      // setMessageOnVideo('');
    } else {
      // setMessageOnVideo('please position yourself inside the box so that your full body is visible.');
    }
  };

  const checkPersonOrientation = () => {
    // Early return if person position monitoring is disabled
    if (!isProperPersonPositionEnable) {
      console.log(
        "person position monitoring disabled, skipping orientation check",
      );
      if (messageOnVideo) {
        // setMessageOnVideo('');
      }
      return;
    }

    const exerciseCodeName = selectedExercise.current?.ExerciseCodeName;
    const faceOrientation = selectedExercise.current?.FaceOrientation;

    // calibration handles orientation internally
    if (exerciseCodeName === CONSTANT.EXERCISES_NAME.calibration) {
      return;
    }

    // // posture analysis requires left orientation
    // if (exerciseCodeName === 'postureAnalysis') {
    //     const correctOrientation = person_orientation_check(poseLandmarks, CONSTANT.MEASUREMENT_KEY.LEFT);
    //     if (!correctOrientation) {
    //         setMessageOnVideo('please turn to face left (side view) with respect to the camera.');
    //     } else {
    //         // clear message when orientation is correct
    //         if (messageOnVideo && messageOnVideo.includes('turn to face')) {
    //             setMessageOnVideo('');
    //         }
    //     }
    //     return;
    // }

    // check if exercise has orientation requirement
    if (ALLOWED_FACE_ORIENTATIONS.includes(faceOrientation)) {
      const correctOrientation = person_orientation_check(
        poseLandmarksRef.current,
        faceOrientation,
      );

      console.log("correctOrientation", faceOrientation, correctOrientation);

      if (correctOrientation !== true) {
        // Wrong orientation - correctOrientation is KeyText string like "leftFacing" or "rightFacing"
        const audio = getAudioByKeyTexts(
          realTimeDetectionConstrains,
          "personOrientation",
          correctOrientation,
        );
        if (shouldAnnounceRealtimeFeedbackNow()) {
          announceRealTimeFeedbackAudio(audio);
        }
      }
    }
  };

  const checkCalibrationRequirements = () => {
    // Early return if realtime feedback is disabled
    if (!isRealtimeFeedbackROMEnable) {
      console.log("realtime feedback disabled, auto-passing calibration check");
      movementStateRef.current.isReadyToRecord = true;
      movementRequirementsRef.current.isReadyForCalibration = true;
      // setMessageOnVideo('');
      return;
    }

    // block if lighting not good
    if (!movementRequirementsRef.current.isGoodLight) {
      calibrationGoodTimestampRef.current = 0;
      movementRequirementsRef.current.isReadyForCalibration = false;
      return;
    }

    // block if multiple persons (only when multi-person detection is enabled)
    if (
      isRealtimeFeedbackROMEnable &&
      isMultiPersonDetectionEnable &&
      movementRequirementsRef.current.isMultiPerson
    ) {
      calibrationGoodTimestampRef.current = 0;
      movementRequirementsRef.current.isReadyForCalibration = false;
      return;
    }

    const result = calibration_monitoring(poseLandmarksRef.current);
    console.log("calibration result----xxx------", result);

    if (result === "good") {
      const now = Date.now();

      if (calibrationGoodTimestampRef.current === 0) {
        // Start debounce timer
        calibrationGoodTimestampRef.current = now;
        console.log("calibration good - starting debounce timer");
        movementRequirementsRef.current.isReadyForCalibration = false;
      } else if (
        now - calibrationGoodTimestampRef.current >=
        CALIBRATION_DEBOUNCE_MS
      ) {
        // Debounce period passed, now ready
        console.log(
          "calibration requirements met (debounced), ready to record",
        );
        movementStateRef.current.isReadyToRecord = true;
        movementRequirementsRef.current.isReadyForCalibration = true;

        // Stop movement-specific checks - calibration is good, no need to check again
        // until recording completes or next movement is selected
        // Only stop for Examinetics - other tenants continue checks until recording starts
        if (isExamineticsEnabled) {
          console.log(
            "calibration requirements met, stopping movement-specific checks interval",
          );
          if (movementSpecificIntervalRef.current) {
            clearInterval(movementSpecificIntervalRef.current);
            movementSpecificIntervalRef.current = null;
          }
        }
      }
      // else: still within debounce period, keep waiting
    } else {
      // Reset debounce timer on any invalid state
      calibrationGoodTimestampRef.current = 0;
      movementStateRef.current.isReadyToRecord = false;
      movementRequirementsRef.current.isReadyForCalibration = false;

      const audio = getAudioByKeyTexts(
        realTimeDetectionConstrains,
        "calibrationMonitoring",
        result,
      );
      if (shouldAnnounceRealtimeFeedbackNow()) {
        announceRealTimeFeedbackAudio(audio);
      }
    }
  };

  const checkPostureRequirements = () => {
    // Early return if realtime feedback is disabled
    if (!isRealtimeFeedbackROMEnable) {
      console.log("realtime feedback disabled, auto-passing posture check");
      movementStateRef.current.isReadyToRecord = true;
      movementRequirementsRef.current.isReadyForCalibration = true;
      // setMessageOnVideo('');
      return;
    }

    // block if multiple persons (only when multi-person detection is enabled)
    if (
      isRealtimeFeedbackROMEnable &&
      isMultiPersonDetectionEnable &&
      movementRequirementsRef.current.isMultiPerson
    ) {
      postureGoodTimestampRef.current = 0;
      movementRequirementsRef.current.isReadyForCalibration = false;
      return;
    }

    // block if any modal is open
    if(isAnyModalOpenRef.current){
      return;
    }

    const result = posture_side_view_monitoring(poseLandmarksRef.current);

    console.log("posture_side_view_monitoring result", result);

    if (result === "good") {
      const now = Date.now();

      if (postureGoodTimestampRef.current === 0) {
        // Start debounce timer
        postureGoodTimestampRef.current = now;
        console.log("posture good - starting debounce timer");
        movementRequirementsRef.current.isReadyForCalibration = false;
      } else if (now - postureGoodTimestampRef.current >= POSTURE_DEBOUNCE_MS) {
        // Debounce period passed, now ready
        console.log("posture requirements met (debounced), ready to record");
        movementStateRef.current.isReadyToRecord = true;
        movementRequirementsRef.current.isReadyForCalibration = true;
        // setMessageOnVideo('');

        // Stop movement-specific checks - posture is good, no need to check again
        // until recording completes or next movement is selected
        // Only stop for Examinetics - other tenants continue checks until recording starts
        if (isExamineticsEnabled) {
          console.log(
            "posture requirements met, stopping movement-specific checks interval",
          );
          if (movementSpecificIntervalRef.current) {
            clearInterval(movementSpecificIntervalRef.current);
            movementSpecificIntervalRef.current = null;
          }
        }
      }
      // else: still within debounce period, keep waiting
    } else {
      // Reset debounce timer on any invalid state
      postureGoodTimestampRef.current = 0;
      movementStateRef.current.isReadyToRecord = false;
      movementRequirementsRef.current.isReadyForCalibration = false;
      const audio = getAudioByKeyTexts(
        realTimeDetectionConstrains,
        "postureSideViewMonitoring",
        result,
      );
      if (shouldAnnounceRealtimeFeedbackNow()) {
        announceRealTimeFeedbackAudio(audio);
      }
    }
  };

  const checkGeneralExerciseRequirements = () => {
    handlePersonVisibility();

    const faceOrientation = selectedExercise.current?.FaceOrientation;

    // Check if exercise requires specific orientation (only if feature is enabled)
    if (
      isRealtimeFeedbackROMEnable &&
      isProperPersonPositionEnable &&
      faceOrientation &&
      ALLOWED_FACE_ORIENTATIONS.includes(faceOrientation)
    ) {
      const correctOrientation = person_orientation_check(
        poseLandmarksRef.current,
        faceOrientation,
      );

      if (correctOrientation === true) {
        const now = Date.now();

        if (orientationGoodTimestampRef.current === 0) {
          orientationGoodTimestampRef.current = now;
          console.log("orientation correct - starting debounce timer");
          movementRequirementsRef.current.isCorrectOrientation = false;
        } else if (
          now - orientationGoodTimestampRef.current >=
          ORIENTATION_DEBOUNCE_MS
        ) {
          console.log(
            "orientation requirements met (debounced), ready to record",
          );
          movementRequirementsRef.current.isCorrectOrientation = true;
          movementStateRef.current.isReadyToRecord = true;
          movementRequirementsRef.current.isReadyForCalibration = true;

          // Stop movement-specific checks - orientation is good, no need to check again
          // until recording completes or next movement is selected
          // Only stop for Examinetics - other tenants continue checks until recording starts
          if (isExamineticsEnabled) {
            console.log(
              "orientation requirements met, stopping movement-specific checks interval",
            );
            if (movementSpecificIntervalRef.current) {
              clearInterval(movementSpecificIntervalRef.current);
              movementSpecificIntervalRef.current = null;
            }
          }
        }
      } else {
        // Wrong or missing orientation - reset
        orientationGoodTimestampRef.current = 0;
        movementRequirementsRef.current.isCorrectOrientation = false;

        // Play audio warning if orientation is wrong (not missing keypoints)
        if (correctOrientation && correctOrientation !== false) {
          const audio = getAudioByKeyTexts(
            realTimeDetectionConstrains,
            "sideMonitoring",
            correctOrientation,
          );
          if (shouldAnnounceRealtimeFeedbackNow()) {
            announceRealTimeFeedbackAudio(audio);
          }
        }
      }
    } else {
      // No orientation requirement - always true
      movementRequirementsRef.current.isCorrectOrientation = true;
    }

    // Only set ready when BOTH visibility AND orientation are correct
    if (
      !movementStateRef.current.isReadyToRecord &&
      movementRequirementsRef.current.isCorrectOrientation
    ) {
      console.log("general exercise ready to record");
      movementStateRef.current.isReadyToRecord = true;
      movementRequirementsRef.current.isReadyForCalibration = true;

      // Stop movement-specific checks - requirements are met, no need to check again
      // until recording completes or next movement is selected
      // Only stop for Examinetics - other tenants continue checks until recording starts
      if (isExamineticsEnabled) {
        console.log(
          "general exercise requirements met, stopping movement-specific checks interval",
        );
        if (movementSpecificIntervalRef.current) {
          clearInterval(movementSpecificIntervalRef.current);
          movementSpecificIntervalRef.current = null;
        }
      }
    } else if (!movementRequirementsRef.current.isCorrectOrientation) {
      movementStateRef.current.isReadyToRecord = false;
      movementRequirementsRef.current.isReadyForCalibration = false;
    }
  };

  // ==================== UNIFIED REQUIREMENT CHECKS ====================
  // Replaces the 3 separate intervals (light, common, movement-specific).
  // Called from onResults every ~10th frame (~300ms at 30fps).
  const runAllRequirementChecks = () => {
    // Time-based throttle: run at most every 300ms regardless of frame rate
    const now = Date.now();
    if (now - lastCheckTimeRef.current < REQUIREMENT_CHECK_INTERVAL_MS) return;
    lastCheckTimeRef.current = now;

    // Re-entry debounce: block all checks for 500ms after person returns to box
    if (reentryTimestampRef.current > 0 && now - reentryTimestampRef.current < REENTRY_DEBOUNCE_MS) {
      return;
    }
    if (reentryTimestampRef.current > 0 && now - reentryTimestampRef.current >= REENTRY_DEBOUNCE_MS) {
      console.log('[DETECTION] re-entry debounce complete, re-running detection');
      reentryTimestampRef.current = 0;
      personWasOutOfBoxRef.current = false;
    }

    if (isMovementPaused.current) return;
    if (isAnyModalOpenRef?.current) return;

    // Skip all requirement checks for non-measurement exercises
    if (!selectedExercise.current?.IsMeasurementNeeded) return;

    // ── Phase 1: Light check (session-level, runs once, independent of FSM state) ──
    if (!sessionLightCheckDoneRef.current) {
      if (!isRealtimeFeedbackROMEnable || !isLightMonitorEnable) {
        console.log('[DETECTION] 1. light monitoring disabled, skipping light check');
        sessionLightCheckDoneRef.current = true;
        movementRequirementsRef.current.isGoodLight = true;
      } else {
        console.log('[DETECTION] 1. running light check');
        performLightCheck();
      }
    }

    // Gate: run person-in-box + full checks only in detection-active FSM states
    const fsmState = detectionBridgeRef?.current?.fsmState;
    const isDetectionActive = fsmState === 'SHOWING_MOVEMENT_INSTRUCTION' ||
      fsmState === 'WAITING_FOR_PERSON_IN_BOX' ||
      fsmState === 'WAITING_FOR_USER_VALIDATION' ||
      fsmState === 'USER_READY_FOR_PRE_RECORDING' ||
      fsmState === 'RUNNING_PRE_RECORDING_COUNTDOWN';

    if (!isDetectionActive) return;

    // If light is not good, stop here
    if (!movementRequirementsRef.current.isGoodLight) {
      return;
    }

    // ── Phase 2: Person in box check ──
    // Skip for Examinetics once orientation is confirmed
    const isCarryTest = selectedExercise.current?.ExerciseCodeName === 'carrytest';
    const isOrientationLocked = isExamineticsEnabled && isCarryTest && movementRequirementsRef.current.isCorrectOrientation;

    if (isOrientationLocked && !GLOBAL.IS_PATIENT_IN_BOX) {
      console.log('[DETECTION] Examinetics: orientation locked, skipping box-out check (person left box)');
    }

    if (!GLOBAL.IS_PATIENT_IN_BOX && !isOrientationLocked) {
      // Reset all detection state immediately when person leaves
      if (movementStateRef.current.personInBoxConfirmed) {
        console.log('[DETECTION] person left box, resetting detection state');
        movementStateRef.current.personInBoxConfirmed = false;
        movementStateRef.current.isReadyToRecord = false;
        movementRequirementsRef.current.isReadyForCalibration = false;
        calibrationGoodTimestampRef.current = 0;
        orientationGoodTimestampRef.current = 0;
        postureGoodTimestampRef.current = 0;
      }
      personWasOutOfBoxRef.current = true;

      if (isExamineticsEnabled) {
        if (examineticsPopupShownRef.current) {
          return; // Pause checks and do nothing while popup is visible
        }

        if (examineticsAudioPlayingRef.current) {
          return; // Wait for current warning audio to finish playing
        }

        const fullBodyAudio = getAudioByKeyTexts(realTimeDetectionConstrains, 'general', 'FULL_BODY_NEGATIVE');
        const now = Date.now();

        if (examineticsOutOfBoxStartTimestampRef.current === 0) {
          // First time out of box
          examineticsOutOfBoxStartTimestampRef.current = now;
          examineticsAnnouncementCountRef.current = 1;
          examineticsLastAnnouncementTimestampRef.current = 0; // Will be set when audio finishes playing
          console.log('[DETECTION] Examinetics: person NOT in box. Playing Announcement 1/3');
          if (fullBodyAudio) {
            examineticsAudioPlayingRef.current = true;
            playAudio(fullBodyAudio).then(() => {
              examineticsAudioPlayingRef.current = false;
              examineticsLastAnnouncementTimestampRef.current = Date.now();
              console.log('[DETECTION] Examinetics: Announcement 1/3 finished playing.');
            }).catch(() => {
              examineticsAudioPlayingRef.current = false;
              examineticsLastAnnouncementTimestampRef.current = Date.now();
            });
          } else {
            examineticsLastAnnouncementTimestampRef.current = now;
          }
        } else {
          // Already out of box, check if 1.5 seconds have elapsed since last announcement finished
          if (now - examineticsLastAnnouncementTimestampRef.current >= 1500) {
            if (examineticsAnnouncementCountRef.current < 3) {
              examineticsAnnouncementCountRef.current++;
              console.log('[DETECTION] Examinetics: person NOT in box. Playing Announcement', examineticsAnnouncementCountRef.current, '/3');
              if (fullBodyAudio) {
                examineticsAudioPlayingRef.current = true;
                playAudio(fullBodyAudio).then(() => {
                  examineticsAudioPlayingRef.current = false;
                  examineticsLastAnnouncementTimestampRef.current = Date.now();
                  console.log('[DETECTION] Examinetics: Announcement', examineticsAnnouncementCountRef.current, '/3 finished playing.');
                }).catch(() => {
                  examineticsAudioPlayingRef.current = false;
                  examineticsLastAnnouncementTimestampRef.current = Date.now();
                });
              } else {
                examineticsLastAnnouncementTimestampRef.current = now;
              }
            } else if (examineticsAnnouncementCountRef.current === 3) {
              examineticsPopupShownRef.current = true;
              console.log('[DETECTION] Examinetics: person NOT in box after 3 announcements. Triggering popup.');
              if (onExamineticsFullBodyNotVisible) {
                onExamineticsFullBodyNotVisible();
              }
            }
          }
        }
      } else {
        if (notInBoxAudioCountRef.current < 4 && !isNotInBoxAudioPlayingRef.current && !audioManager.getIsPlaying()) {
          const fullBodyAudio = getAudioByKeyTexts(realTimeDetectionConstrains, 'general', 'FULL_BODY_NEGATIVE');
          if (fullBodyAudio) {
            console.log('[DETECTION] 2. person NOT in box, playing FULL_BODY_NEGATIVE audio (attempt', notInBoxAudioCountRef.current + 1, '/4)');
            isNotInBoxAudioPlayingRef.current = true;
            playAudio(fullBodyAudio).then((result) => {
              if (result === "Audio finished playing.") {
                notInBoxAudioCountRef.current++;
              }
              isNotInBoxAudioPlayingRef.current = false;
            });
          }
        } else if (notInBoxAudioCountRef.current >= 4 && !breakPausePlayedRef.current && !isNotInBoxAudioPlayingRef.current && !audioManager.getIsPlaying()) {
          const breakPauseAudio = getAudioUrl(CONSTANT.GENERAL_AUDIOS_KEY_TEXT.TAKE_BRAKE);
          if (breakPauseAudio) {
            console.log('[DETECTION] 2. person NOT in box, playing BreakPause audio');
            isNotInBoxAudioPlayingRef.current = true;
            playAudio(breakPauseAudio).then((result) => {
              if (result === "Audio finished playing.") {
                breakPausePlayedRef.current = true;
              }
              isNotInBoxAudioPlayingRef.current = false;
            });
          }
        } else if (breakPausePlayedRef.current) {
          console.log('[DETECTION] 2. person NOT in box, waiting silently (BreakPause already played)');
        }
      }
      // Stop proceeding, wait for user to return to box
      return;
    } else {
      if (isExamineticsEnabled) {
        if (examineticsOutOfBoxStartTimestampRef.current !== 0) {
          console.log('[DETECTION] Examinetics: person back in box, resetting announcement tracking');
          examineticsOutOfBoxStartTimestampRef.current = 0;
          examineticsAnnouncementCountRef.current = 0;
          examineticsLastAnnouncementTimestampRef.current = 0;
        }
      }
      if (personWasOutOfBoxRef.current && reentryTimestampRef.current === 0) {
        console.log('[DETECTION] person back in box, waiting 500ms before re-validation');
        reentryTimestampRef.current = Date.now();
      }
      notInBoxAudioCountRef.current = 0;
      breakPausePlayedRef.current = false;
    }

    // Landmarks required for orientation and calibration checks
    if (!poseLandmarksRef.current || poseLandmarksRef.current.length === 0) return;

    // ── Phase 3: Full checks (orientation + movement-specific) ──
    // Only runs in WAITING_FOR_USER_VALIDATION onwards
    const isFullCheckState = fsmState === 'WAITING_FOR_USER_VALIDATION' ||
      fsmState === 'USER_READY_FOR_PRE_RECORDING' ||
      fsmState === 'RUNNING_PRE_RECORDING_COUNTDOWN';

    if (!isFullCheckState) return;

    // Sync internal ref from FSM state via bridge
    isShowCountDownRef.current = detectionBridgeRef?.current?.fsmState === 'RUNNING_PRE_RECORDING_COUNTDOWN';

    // 4. Person in box confirmation (stops once confirmed per movement)
    if (!movementStateRef.current.personInBoxConfirmed && GLOBAL.IS_PATIENT_IN_BOX) {
      console.log('[DETECTION] 3. checking person in box');
      if (isRealtimeFeedbackROMEnable && isProperPersonPositionEnable) {
        checkPersonInBox();
      } else {
        movementStateRef.current.personInBoxConfirmed = true;
      }
    }

    // 5. Person orientation (runs until person is validated, then stops)
    if (movementStateRef.current.personInBoxConfirmed && !movementStateRef.current.isReadyToRecord) {
      if (isRealtimeFeedbackROMEnable && isProperPersonPositionEnable) {
        if (!isAnyModalOpenRef?.current) {
          console.log('[DETECTION] 4. checking face orientation');
          checkPersonOrientation();
        }
      }
    }

    // 6. Movement-specific checks (calibration/posture/general)
    // Only run when person is in box and not yet ready to record
    // For Examinetics with orientation locked, skip box requirement
    if (
      (GLOBAL.IS_PATIENT_IN_BOX || isOrientationLocked) &&
      !openVideoModal.current &&
      !isCalibrationProcessing.current &&
      (!isExamineticsEnabled || !isShowCountDownRef.current || selectedExercise.current?.ExerciseCodeName !== 'carrytest') &&
      !movementStateRef.current.isReadyToRecord &&
      !isCurrentMovementRecordingRef.current
    ) {
      const exerciseCodeName = selectedExercise.current?.ExerciseCodeName;

      if (exerciseCodeName === CONSTANT.EXERCISES_NAME.calibration) {
        checkCalibrationRequirements();
      } else if (exerciseCodeName === CONSTANT.EXERCISES_NAME.postureAnalysis) {
        checkPostureRequirements();
      } else {
        checkGeneralExerciseRequirements();
      }
    }
  };

  const startCaptureHandler = (duration) => {
    if (webcamRef.current && webcamRef.current.srcObject) {
      VideoRecorder.init(
        webcamRef.current.srcObject,
        onStopVideoRecording,
        duration ?? totalSeconds,
      );
      VideoRecorder.startRecording();
    }
  };

  // ==================== RECORDING-LEVEL: MULTI-PERSON DETECTION ====================
  // runs at 1 second intervals during 5-second recording
  const startMultiPersonDetection = async () => {
    if (!isMultiPersonDetectionEnable) {
      console.log("multi-person detection disabled, skipping");
      return;
    }

    if (multiPersonIntervalRef.current) {
      clearInterval(multiPersonIntervalRef.current);
    }

    multiPersonCheckCountRef.current = 0;

    // Detector should already be loaded from background initialization
    // If not loaded yet, wait briefly for it to finish
    if (!moveNetDetectorRef.current && isDetectorLoadingRef.current) {
      console.log("detector still loading, waiting up to 2 seconds...");
      const maxWait = 2000;
      const checkInterval = 100;
      let waited = 0;
      while (
        !moveNetDetectorRef.current &&
        isDetectorLoadingRef.current &&
        waited < maxWait
      ) {
        await new Promise((resolve) => setTimeout(resolve, checkInterval));
        waited += checkInterval;
      }
    }

    if (!moveNetDetectorRef.current) {
      console.error(
        "moveNet detector not available, multi-person detection disabled for this recording",
      );
      return;
    }

    console.log("starting multi-person detection interval (detector ready)");
    // Start interval - detector is confirmed ready
    multiPersonIntervalRef.current = setInterval(async () => {
      multiPersonCheckCountRef.current += 1;

      if (multiPersonCheckCountRef.current <= 5) {
        console.log(`multi-person check ${multiPersonCheckCountRef.current}/5`);
        await performMultiPersonCheck();
      } else {
        console.log("completed all 5 multi-person checks");
        clearInterval(multiPersonIntervalRef.current);
      }
    }, 1000);
  };

  const performMultiPersonCheck = async () => {
    const detector = moveNetDetectorRef.current;

    if (!detector || typeof detector.estimatePoses !== "function") {
      try {
        const loaded = await loadMoveNetDetector();
        if (loaded && typeof loaded.estimatePoses === "function") {
          moveNetDetectorRef.current = loaded;
          await performMultiPersonCheckCore(loaded);
        }
      } catch (error) {
        console.error("failed to load movenet detector:", error);
      }
      return;
    }

    await performMultiPersonCheckCore(detector);
  };

  const startRecordingImperative = useCallback((duration) => {
    if (isCurrentMovementRecordingRef.current) return;
    console.log("=== starting recording (imperative) ===");
    isCurrentMovementRecordingRef.current = true;
    startCaptureHandler(duration);

    if (isRealtimeFeedbackROMEnable && isMultiPersonDetectionEnable) {
      startMultiPersonDetection();
    }
  }, []);

  useEffect(() => {
    if (imperativeRecordingRef) {
      imperativeRecordingRef.current = startRecordingImperative;
    }
  }, [startRecordingImperative, imperativeRecordingRef]);

  // stop all intervals if assessment is complete
  useEffect(() => {
    if (isAssessmentComplete) {
      console.log("Assessment complete - clearing all VideoPanel intervals");
      clearAllIntervals();
    }
  }, [isAssessmentComplete]);

  // update performMultiPersonCheckCore to handle the restart flow
  const performMultiPersonCheckCore = async (detector) => {
    // Early return if multi-person detection is disabled
    if (!isMultiPersonDetectionEnable) {
      console.log("multi-person detection disabled, skipping check");
      movementRequirementsRef.current.isMultiPerson = false;
      return;
    }

    try {
      const result = await detectMultiplePersons(webcamRef.current, detector);

      if (result.hasMultiple) {
        // notify parent to reset its state
        if (onMultiPersonDetected) {
          await onMultiPersonDetected();
        }

        console.log("=== multiple persons detected during recording ===");

        // immediately stop recording
        VideoRecorder.stopRecording();

        // stop multi-person checks
        clearInterval(multiPersonIntervalRef.current);

        // notify user
        const audio = getAudioByKeyTexts(
          realTimeDetectionConstrains,
          "multiplePerson",
          "multiplePersonDetected",
        );
        if (shouldAnnounceMultiPersonFeedbackNow()) {
          announceRealTimeFeedbackAudio(audio);
        }

        console.log(
          "recording terminated, movement checks will restart automatically",
        );

        // reset movement-level state
        resetMovementLevelState();

        movementRequirementsRef.current.isMultiPerson = false;
        forceCloseReviewMovementModalRef.current = false;
      } else {
        // clear multi-person flag if previously set
        if (movementRequirementsRef.current.isMultiPerson) {
          console.log("single person confirmed");
          movementRequirementsRef.current.isMultiPerson = false;
          forceCloseReviewMovementModalRef.current = false;
          if (messageOnVideo.includes("multiple people")) {
            // setMessageOnVideo('');
          }
        }
      }
    } catch (error) {
      console.error("error during multi-person detection:", error);
    }
  };

  const handlePhaseProcessing = () => {
    const currentPhase = processPhase(
      poseLandmarksRef.current,
      selectedExercise.current?.KeyPointRestriction,
    );

    const { prevPhase } = stateRef.current;
    stateRef.current.prevPhase = stateRef.current.currentPhase;
    stateRef.current.currentPhase = currentPhase;

    const kpr = selectedExercise.current?.KeyPointRestriction || [];

    const phaseMap = {};
    kpr.forEach((p) => {
      phaseMap[p.PhaseDialogue] = p.PhaseNumber;
    });

    if (stateRef.current.inRepCycle === undefined) {
      stateRef.current.inRepCycle = false;
      stateRef.current.reps = 0;
      stateRef.current.phaseJourney = [];
      stateRef.current.repStartPhase = null;
    }

    if (
      currentPhase !== CONSTANT.REP_PHASE.UNKNOWN &&
      currentPhase !== prevPhase
    ) {
      const phaseNo = phaseMap[currentPhase];

      if (!stateRef.current.inRepCycle) {
        stateRef.current.inRepCycle = true;
        stateRef.current.repStartPhase = phaseNo;
        stateRef.current.phaseJourney = [phaseNo];
      } else if (
        phaseNo === stateRef.current.repStartPhase &&
        stateRef.current.phaseJourney.length > 1
      ) {
        stateRef.current.reps += 1;
        setRepCount(stateRef.current.reps);

        stateRef.current.inRepCycle = false;
        stateRef.current.phaseJourney = [];
        stateRef.current.repStartPhase = null;
      } else if (!stateRef.current.phaseJourney.includes(phaseNo)) {
        stateRef.current.phaseJourney.push(phaseNo);
      }
    }
  };

  const handlePersonVisibility = () => {
    const kpr = selectedExercise.current?.KeyPointRestriction;
    const result = checkPersonVisibility(poseLandmarksRef.current, kpr);
    if (!messageOnVideo && !result) {
      // setMessageOnVideo('');
    }
  };

  const loadMediaPipe = async () => {
    // Only create a new Pose instance if one doesn't already exist
    if (poseModelRef.current) {
      console.log("MediaPipe Pose already initialized, resuming processing");
      // Resume processing if it was paused during camera switch
      isCameraSwitchingRef.current = false;
      if (!isProcessingRef.current) {
        startProcessing();
      }
      return;
    }

    console.log("loadMediaPipe: Initializing MediaPipe Pose instance");
    const poseModel = new Pose({
      locateFile: (file) =>
        `https://cdn.jsdelivr.net/npm/@mediapipe/pose/${file}`,
    });

    poseModel.setOptions({
      modelComplexity: 1,
      smoothLandmarks: true,
      enableSegmentation: false,
      smoothSegmentation: true,
      minDetectionConfidence: 0.5,
      minTrackingConfidence: 0.5,
    });

    poseModel.onResults(onResults);
    console.log("loadMediaPipe: loaded MediaPipe Pose instance");

    // Store the pose model reference
    poseModelRef.current = poseModel;

    startProcessing();
  };

  // Draw video frame + last known skeleton (called every animation frame)
  const drawFrameOnly = () => {
    if (!canvasRef.current || !webcamRef.current) return;

    const canvasCtx = canvasRef.current.getContext("2d");

    // Reset to identity before clearing (no accumulated transforms)
    canvasCtx.setTransform(1, 0, 0, 1, 0, 0);
    canvasCtx.clearRect(
      0,
      0,
      canvasRef.current.width,
      canvasRef.current.height,
    );

    // Draw video frame + bounding box (sets mirror transform via setTransform)
    drawCtx(webcamRef.current);

    // Draw segmentation mask if present
    if (segmentationMaskRef.current) {
      canvasCtx.drawImage(
        segmentationMaskRef.current,
        0,
        0,
        canvasRef.current.width,
        canvasRef.current.height,
      );
    }

    // Draw last known skeleton
    if (poseLandmarksRef.current && poseLandmarksRef.current.length > 0) {
      const landmarks = poseLandmarksRef.current.map((landmark) => ({
        x: landmark.x * canvasRef.current.width,
        y: landmark.y * canvasRef.current.height,
        visibility: landmark.visibility,
      }));

      const fillColour = FULL_SCREEN_SKELETON_COLOR || "#5578EB";
      drawConnectorsCustom(
        canvasCtx,
        landmarks,
        POSE_CONNECTIONS,
        fillColour,
        2,
      );
      drawLandmarksCustom(canvasCtx, landmarks, fillColour, 2, 3);
    }
  };

  const startProcessing = () => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    let lastProcessTime = 0;
    const activeFrameTime = 1000 / 15;  // ~67ms for 15fps
    const idleFrameTime = 1000 / 5;     // 200ms for 5fps

    const getMinFrameTime = () => {
      const isActive = isCurrentMovementRecordingRef.current ||
        startRecording.current ||
        isShowCountDownRef.current ||
        detectionBridgeRef?.current?.fsmState === 'USER_READY_FOR_PRE_RECORDING';
      return isActive ? activeFrameTime : idleFrameTime;
    };

    const animate = (timestamp) => {
      if (isCameraSwitchingRef.current) {
        console.log("animate: disable if switching camera");
        isProcessingRef.current = false;
        return;
      }

      const minFrameTime = getMinFrameTime();

      if (
        poseModelRef.current &&
        webcamRef.current &&
        webcamRef.current.srcObject
      ) {
        if (timestamp - lastProcessTime >= minFrameTime) {
          lastProcessTime = timestamp;
          frameCountRef.current++;

          // Always draw video + last skeleton on every frame
          drawFrameOnly();

          poseModelRef.current
            .send({ image: webcamRef.current })
            .then(() => {
              if (!isCameraSwitchingRef.current) {
                requestRef.current = requestAnimationFrame(animate);
              }
            })
            .catch((error) => {
              console.error("MediaPipe send error:", error);
              isProcessingRef.current = false;
            });
        } else {
          requestRef.current = requestAnimationFrame(animate);
        }
      } else {
        requestRef.current = requestAnimationFrame(animate);
      }
    };

    requestRef.current = requestAnimationFrame(animate);
  };

  const onResults = (results) => {
    if (!canvasRef.current) return;

    // Store segmentation mask for drawFrameOnly to render
    segmentationMaskRef.current = results.segmentationMask || null;

    if (results.poseLandmarks) {
      if (!firstPoseLogged.current) {
        console.log("first pose landmarks received");
        firstPoseLogged.current = true;
      }

      poseLandmarksRef.current = results.poseLandmarks;

      const landmarks = results.poseLandmarks.map((landmark) => ({
        x: landmark.x * canvasRef.current.width,
        y: landmark.y * canvasRef.current.height,
        visibility: landmark.visibility,
      }));

      if (selectedExercise.current?.IsMeasurementNeeded === false) {
        GLOBAL.IsColorRedForFullBox = false;
        GLOBAL.IS_PATIENT_IN_BOX = true;
      } else {
        GLOBAL.IsColorRedForFullBox = !getFullBodyInBox(landmarks);
        GLOBAL.IS_PATIENT_IN_BOX = !GLOBAL.IsColorRedForFullBox;
      }

      if(!isAnyModalOpenRef.current && selectedExercise.current?.IsMeasurementNeeded !== false){
        if (!GLOBAL.IS_PATIENT_IN_BOX) {
          const isCountdownActive = isShowCountDownRef.current || detectionBridgeRef?.current?.fsmState === 'USER_READY_FOR_PRE_RECORDING';
          const isRecordingActive = startRecording.current || isCurrentMovementRecordingRef.current;

          if(isEnableAutoStopOnOutOfFrame && selectedExercise.current.ExerciseCodeName !== 'carrytest'){
            if (isRecordingActive) {
              console.log("=== immediately stopping recording (left box) ===");
              VideoRecorder.stopRecording();
              if (onRecordingInterrupted) {
                onRecordingInterrupted(true);
              }
            } else if (isCountdownActive) {
              if (onRecordingInterrupted) {
                onRecordingInterrupted(false);
              }
              resetMovementLevelState();
            }
          }
        }
      }

      // Process phase for every frame when conditions are met
      if (
        selectedExercise.current?.ExerciseTypeId ===
          CONSTANT.MOVEMENT_TYPES.STRENGTH ||
        selectedExercise.current?.ExerciseTypeId ===
          CONSTANT.MOVEMENT_TYPES.HYBRID ||
        isCurrentMovementRecordingRef.current
      ) {
        handlePhaseProcessing();
      }
    } else {
      if (selectedExercise.current?.IsMeasurementNeeded === false) {
        GLOBAL.IsColorRedForFullBox = false;
        GLOBAL.IS_PATIENT_IN_BOX = true;
      } else {
        GLOBAL.IsColorRedForFullBox = true;
        GLOBAL.IS_PATIENT_IN_BOX = false;
        GLOBAL.IS_PATIENT_FACING_LEFT = false;
        GLOBAL.IS_PATIENT_FACING_RIGHT = false;
      }

      // Clear stale landmarks so drawFrameOnly stops rendering frozen keypoints
      poseLandmarksRef.current = [];

      if (messageOnVideo) {
        // setMessageOnVideo('');
      }
    }

    // Run all requirement checks inline instead of via intervals
    runAllRequirementChecks();

    // Write detection snapshot to bridge for FSM processing
    if (detectionBridgeRef?.current) {
      const isNonMeasurement = !selectedExercise.current?.IsMeasurementNeeded;
      const isCarryTest = selectedExercise.current?.ExerciseCodeName === 'carrytest';
      const isOrientationLocked = isExamineticsEnabled && isCarryTest && movementRequirementsRef.current.isCorrectOrientation;
      if (isOrientationLocked && !GLOBAL.IS_PATIENT_IN_BOX) {
        console.log('[DETECTION] Examinetics: orientation locked, overriding personInBox=true in detection snapshot');
      }
      detectionBridgeRef.current.lastDetection = {
        isGoodLight: isNonMeasurement ? true : movementRequirementsRef.current.isGoodLight,
        isReadyForCalibration: isNonMeasurement ? true : movementRequirementsRef.current.isReadyForCalibration,
        isCorrectOrientation: isNonMeasurement ? true : movementRequirementsRef.current.isCorrectOrientation,
        isMultiPerson: isNonMeasurement ? false : movementRequirementsRef.current.isMultiPerson,
        personInBox: (isNonMeasurement || isOrientationLocked) ? true : GLOBAL.IS_PATIENT_IN_BOX,
        isColorRedForFullBox: (isNonMeasurement || isOrientationLocked) ? false : GLOBAL.IsColorRedForFullBox,
      };
      detectionBridgeRef.current.detectionVersion = (detectionBridgeRef.current.detectionVersion || 0) + 1;
    }
  };

  const drawWalkingTestLines = (ctx) => {
    const exerciseDisplayName = selectedExercise.current?.ExerciseDisplayName;
    if (exerciseDisplayName?.toLowerCase() === 'carry test') {
      const videoWidth = canvasRef.current.width;
      const videoHeight = canvasRef.current.height;
      const canvasMidHeight = videoHeight / 2;
 
      ctx.beginPath();
      ctx.lineWidth = 5;
      ctx.strokeStyle = "#E10600";
 
      // Middle Horizontal Line
      ctx.moveTo(150, canvasMidHeight + 50);
      ctx.lineTo(videoWidth - 150, canvasMidHeight + 50);
 
      // Bottom Horizontal Line
      ctx.moveTo(100, videoHeight - 10);
      ctx.lineTo(videoWidth - 100, videoHeight - 10);
 
      ctx.stroke();
    }
  };

  const drawCtx = (video) => {
    if (canvasRef.current) {
      let ctx = canvasRef.current.getContext("2d");

      const videoWidth = video.videoWidth;
      const videoHeight = video.videoHeight;

      video.width = videoWidth;
      video.height = videoHeight;

      // Only resize canvas when dimensions actually change (avoids expensive buffer reallocation)
      const dimsChanged = lastCanvasDimsRef.current.width !== videoWidth || lastCanvasDimsRef.current.height !== videoHeight;
      if (dimsChanged) {
        canvasRef.current.width = videoWidth;
        canvasRef.current.height = videoHeight;
        lastCanvasDimsRef.current = { width: videoWidth, height: videoHeight };
      }

      // Set absolute mirror transform (not relative) — prevents accumulation when canvas isn't resized
      ctx.setTransform(-1, 0, 0, 1, videoWidth, 0);

      ctx.beginPath();
      let canvasMidWidth = canvasRef.current.width / 2;
      let canvasMidHeight = canvasRef.current.height / 2;

      if (GLOBAL.DEVICE_WIDTH < 1000) {
        if (GLOBAL.DRAW_BOX) {
          GLOBAL.left = canvasMidWidth - (canvasMidWidth * 3) / 4;
        } else {
          GLOBAL.left = 2;
        }
      } else {
        if (GLOBAL.DRAW_BOX) {
          GLOBAL.left = canvasMidWidth - canvasMidWidth / 2;
        } else {
          GLOBAL.left = 2;
        }
      }

      let right, bottom;
      if (GLOBAL.DRAW_BOX) {
        right = 2 * (canvasMidWidth - GLOBAL.left);
        GLOBAL.top = canvasMidHeight - (canvasMidHeight * 19) / 20;
        bottom = 2 * (canvasMidHeight - GLOBAL.top);
      } else {
        right = videoWidth - GLOBAL.left;
        GLOBAL.top = 2;
        bottom = videoHeight - GLOBAL.top;
      }
      GLOBAL.right = right + GLOBAL.left;
      GLOBAL.bottom = bottom;

      const exerciseDisplayName = selectedExercise.current?.ExerciseDisplayName;
      const isCarryTest = exerciseDisplayName?.toLowerCase() === 'carry test';

      if (GLOBAL.DRAW_BOX && !isCarryTest) {
        ctx.rect(GLOBAL.left, GLOBAL.top, right, bottom);
        ctx.lineWidth = 5;
        ctx.strokeStyle = GLOBAL.IsColorRedForFullBox ? "#E10600" : "#AAFF00";
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(canvasMidWidth, 0);
        ctx.lineTo(canvasMidWidth, canvasRef.current.height);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(0, canvasMidHeight);
        ctx.lineTo(canvasRef.current.width, canvasMidHeight);
        ctx.stroke();
      }
      
      // drawWalkingTestLines(ctx);
      ctxRef.current = ctx;
    }
  };

  function drawConnectorsCustom(ctx, landmarks, connections, color, lineWidth) {
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.beginPath();

    connections.forEach(([startIdx, endIdx]) => {
      const start = landmarks[startIdx];
      const end = landmarks[endIdx];
      if (start?.visibility < 0.5 || end?.visibility < 0.5) return;
      if (start && end) {
        ctx.moveTo(start.x, start.y);
        ctx.lineTo(end.x, end.y);
      }
    });
    ctx.stroke();
  }

  function drawLandmarksCustom(ctx, landmarks, color, lineWidth, radius) {
    ctx.fillStyle = color;

    landmarks.forEach((landmark, index) => {
      if (landmark.visibility < 0.5) return;

      ctx.fillStyle = color;
      ctx.strokeStyle = color;

      const circle = new Path2D();
      circle.arc(landmark.x, landmark.y, radius, 0, 2 * Math.PI);
      ctx.stroke(circle);
    });

    const sholderMidPoint = getShoulderMidPoint(landmarks);
    const circle = new Path2D();
    circle.arc(sholderMidPoint.x, sholderMidPoint.y, radius, 0, 2 * Math.PI);
    ctx.stroke(circle);
  }

  const onUserMediaStream = async () => {
    console.log("webcam stream started, loading movenet and mediapipe");
    GLOBAL.IS_CAMERA_READY_FOR_BIOMETRIC = true;

    // Start loading moveNet detector in background (non-blocking)
    // This ensures it's ready when multi-person detection is needed
    if (
      isMultiPersonDetectionEnable &&
      !isDetectorLoadingRef.current &&
      !moveNetDetectorRef.current
    ) {
      console.log("starting background load of moveNet detector");
      isDetectorLoadingRef.current = true;
      loadMoveNetDetector()
        .then((loaded) => {
          if (loaded && typeof loaded.estimatePoses === "function") {
            moveNetDetectorRef.current = loaded;
            console.log(
              "moveNet detector loaded in background (ready for use)",
            );
          } else {
            console.error("loaded moveNet detector is invalid");
          }
        })
        .catch((error) => {
          console.error(
            "failed to load moveNet detector in background:",
            error,
          );
        })
        .finally(() => {
          isDetectorLoadingRef.current = false;
        });
    }

    console.log("starting mediapipe");
    loadMediaPipe();
  };

  const onUserMediaError = (e) => {
    console.error("camera error:", e);
    setCount((prevState) => prevState + 1);
  };

  // ==================== CLEANUP ====================
  useEffect(() => {
    return () => {
      clearAllIntervals();
      // Clean up MediaPipe Pose instance
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
        requestRef.current = null;
      }
      poseModelRef.current = null;
    };
  }, []);

  // ==================== SESSION-LEVEL: LIGHT CHECK (DISABLED CASE) ====================
  // When light monitoring is disabled, mark it as done immediately.
  // When enabled, performLightCheck() is called from runAllRequirementChecks() in onResults.
  // ==================== MOVEMENT-LEVEL: RESET ON EXERCISE CHANGE ====================
  useEffect(() => {
    resetMovementLevelState();
  }, [exerciseGeneration]);

  // Recording is now managed by FSM via imperativeRecordingRef.
  // See MovementCore → fsmActions.startRecording → imperativeRecordingRef → startCaptureHandler.

  // ==================== REP COUNTING ====================
  // Phase processing is now handled directly in onResults callback for every frame

  // ==================== MEDIAPIPE & RENDERING ====================

  // Handle camera switching - pause MediaPipe processing during switch
  useEffect(() => {
    const deviceId = webcamConstraints?.deviceId;

    return () => {
      // Cleanup when deviceId is about to change
      if (deviceId && poseModelRef.current) {
        console.log("Camera switching, pausing MediaPipe processing");
        isCameraSwitchingRef.current = true;
        isProcessingRef.current = false;
        lastCanvasDimsRef.current = { width: 0, height: 0 }; // reset for new camera

        // Cancel any pending animation frames
        if (requestRef.current) {
          cancelAnimationFrame(requestRef.current);
          requestRef.current = null;
        }
      }
    };
  }, [webcamConstraints?.deviceId]);

  const webcamNewComponent = useMemo(() => {
    return (
      <UserMediaWebcam
        webcamRef={webcamRef}
        style={{
          height: "100%",
          width: "100%",
          objectFit: "fill",
          position: "absolute",
        }}
        mirrored={true}
        videoConstraints={webcamConstraints}
        onUserMedia={onUserMediaStream}
        onUserMediaError={onUserMediaError}
      />
    );
  }, [webcamConstraints, count]);

  return (
    <>
      {webcamNewComponent}
      <canvas
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          objectFit: "fill",
          zIndex: 0,
        }}
        ref={canvasRef}
      />
    </>
  );
};

export default VideoPanel;
